import { lazy, useEffect, useReducer, useRef } from "react";
import { matchPath } from "react-router-dom";
import { getCaseStudyLayout } from "../data/caseStudyIndex";

// Every page except Home is its own chunk, and so is each editorial case study
// (with its narrative and diagrams). Home stays in the main bundle: it is the
// page most visits start on, so splitting it would only add a request.
//
// Measured before this: one 1.13 MB main.js (339 KB gzipped) holding all nine
// pages, the lightbox, and every case study's copy - parsed and run in full
// before a phone could paint the homepage.
//
// Page transitions are why this is more than React.lazy. PageTransition fades
// the old page out and the new one in, keyed on the path; if the new page were
// still a pending chunk, the fade would reveal an empty Suspense fallback. So
// the app shell keeps showing the previous location until the next page's code
// has arrived (useReadyLocation), then transitions as it always did. Work and
// the case-study page are downloaded ahead of time (webpackPrefetch below), and a
// link loads its page on a settled hover, mouse-down or keyboard focus
// (RoutePrefetch.jsx), so a normal click rarely waits.

const chunk = (load) => {
  let module = null;
  let promise = null;
  const preload = () =>
    (promise ??= load().then(
      (m) => (module = m),
      (error) => {
        promise = null; // let the next attempt try the network again
        throw error;
      }
    ));
  const Lazy = lazy(preload);
  // Once loaded, render the module directly: no Suspense round trip at all.
  const Component = (props) => {
    const Page = module?.default;
    return Page ? <Page {...props} /> : <Lazy {...props} />;
  };
  return { preload, isReady: () => module !== null, Component };
};

export const chunks = {
  // Prefetched: webpack downloads these two at idle network priority once main.js
  // has loaded, without running them, because nearly every visit goes to one next.
  work: chunk(() => import(/* webpackChunkName: "work", webpackPrefetch: true */ "../pages/Work/Work")),
  caseStudy: chunk(() => import(/* webpackChunkName: "case-study", webpackPrefetch: true */ "../pages/CaseStudy/CaseStudy")),
  editorial: chunk(() => import(/* webpackChunkName: "cs-claims" */ "../pages/CaseStudy/EditorialCaseStudy")),
  editorialExpense: chunk(() => import(/* webpackChunkName: "cs-expense" */ "../pages/CaseStudy/SharedExpenseCaseStudy")),
  editorialGold: chunk(() => import(/* webpackChunkName: "cs-gold" */ "../pages/CaseStudy/GoldVaultCaseStudy")),
  liveDemos: chunk(() => import(/* webpackChunkName: "live-demos" */ "../pages/LiveDemos/LiveDemos")),
  uxWriting: chunk(() => import(/* webpackChunkName: "ux-writing" */ "../pages/UxWriting/UxWriting")),
  writingSamples: chunk(() => import(/* webpackChunkName: "writing-samples" */ "../pages/WritingSamples/WritingSamples")),
  about: chunk(() => import(/* webpackChunkName: "about" */ "../pages/About/About")),
  resume: chunk(() => import(/* webpackChunkName: "resume" */ "../pages/Resume/Resume")),
  contact: chunk(() => import(/* webpackChunkName: "contact" */ "../pages/Contact/Contact")),
};

// Keyed on caseStudies.js `layout`. CaseStudy.jsx renders these.
export const EDITORIAL_CHUNKS = {
  editorial: chunks.editorial,
  "editorial-expense": chunks.editorialExpense,
  "editorial-gold": chunks.editorialGold,
};

const PATHS = {
  "/work": chunks.work,
  "/live-demos": chunks.liveDemos,
  "/ux-writing": chunks.uxWriting,
  "/writing-samples": chunks.writingSamples,
  "/about": chunks.about,
  "/resume": chunks.resume,
  "/contact": chunks.contact,
};

// The chunks a path needs before it can render. Unknown paths need none.
export const chunksFor = (pathname) => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : "/";
  const study = matchPath("/work/:slug", path);
  if (study) {
    const editorial = EDITORIAL_CHUNKS[getCaseStudyLayout(study.params.slug)];
    return editorial ? [chunks.caseStudy, editorial] : [chunks.caseStudy];
  }
  return PATHS[path] ? [PATHS[path]] : [];
};

export const isRouteReady = (pathname) => chunksFor(pathname).every((c) => c.isReady());

export const preloadRoute = (pathname) => Promise.all(chunksFor(pathname).map((c) => c.preload()));

// A chunk that fails to load after a deploy usually means this tab is running
// the previous build, whose hashed files are gone. A full page load fetches the
// current one. Once per path per session, so an offline visitor is not looped.
const reloadOnce = (location) => {
  const key = `chunk-reload:${location.pathname}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch {
    return;
  }
  window.location.assign(location.pathname + location.search + location.hash);
};

// The location the app should render: the requested one once its code is
// loaded, and until then the one already on screen.
export const useReadyLocation = (location) => {
  const [, rerender] = useReducer((n) => n + 1, 0);
  const shown = useRef(location);
  const ready = isRouteReady(location.pathname);
  if (ready) shown.current = location;

  useEffect(() => {
    if (ready) return undefined;
    let current = true;
    preloadRoute(location.pathname).then(
      () => current && rerender(),
      () => current && reloadOnce(location)
    );
    return () => {
      current = false;
    };
  }, [ready, location]);

  return ready ? location : shown.current;
};
